import { forwardRef } from 'react';
import classnames from 'classnames';

import '@hh.ru/magritte-common-focus-visible';
import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { ActionSkeleton } from '@hh.ru/magritte-ui-action/Skeleton';
import { type ActionProps } from '@hh.ru/magritte-ui-action/types';
import { BadgeSize, ComponentWithBadge } from '@hh.ru/magritte-ui-badge';
import { enrichWithSkeleton } from '@hh.ru/magritte-ui-skeleton';

import styles from './action.less';

const ALLOWED_BADGE_SIZES: BadgeSize[] = ['small', 'extra-small'];

export const ActionComponent = forwardRef<HTMLButtonElement, ActionProps>(
    (
        {
            style = 'neutral',
            pressedStyle,
            hoverStyle,
            mode = 'primary',
            icon,
            showBackground = false,
            onClick,
            disabled,
            disablePadding = false,
            badge,
            'data-qa': dataQa,
            ...rest
        },
        ref
    ) => {
        const IconComponent = icon;
        const forceDisabled = useDisabled();
        const isComponentDisabled = !!disabled || forceDisabled;

        const action = (
            <button
                {...rest}
                ref={ref}
                type="button"
                onClick={onClick}
                className={classnames(
                    styles.action,
                    styles[`action-mode-${mode}`],
                    styles[`action-style-${style}`],
                    styles[`action-hover-style-${hoverStyle || style}`],
                    styles[`action-pressed-style-${pressedStyle || style}`],
                    {
                        [styles.actionShowBackground]: showBackground && !disablePadding,
                        [styles.actionDisablePadding]: disablePadding,
                    }
                )}
                disabled={isComponentDisabled}
                data-qa={dataQa}
            >
                <IconComponent />
            </button>
        );

        if (!badge || isComponentDisabled) {
            return action;
        }

        return (
            <ComponentWithBadge allowedSizes={ALLOWED_BADGE_SIZES} badge={badge} offset={disablePadding ? -3 : 5}>
                {action}
            </ComponentWithBadge>
        );
    }
);

ActionComponent.displayName = 'Action';
const ActionWithFlag = ActionComponent as typeof ActionComponent & { isAction: true };
ActionWithFlag.isAction = true;

export const Action = enrichWithSkeleton(ActionComponent, ActionSkeleton);
